import { ENDPOINTS } from 'config/endpoints';
import { QueryClient, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { ResponsePaginated } from 'crono-fe-common/types/response';
import { ActivityType } from 'crono-fe-common/types/enums/activityType';
import { PreviousActivitiesDTO } from 'crono-fe-common/types/DTO/activitiesDTO';

const ENDPOINT = ENDPOINTS.events.activities.search;

export interface ActivitiesFilters {
  limit: number;
  page: number;
  accountId: string | null;
  prospectId: string | null;
  opportunityId: string | null;
  types: ActivityType[] | null;
}

// Both the paged and infinite activities queries key off
// ENDPOINTS.events.activities.search as their leading segment, so a plain
// prefix match invalidates every cached Previous Activities list.
export const invalidateActivitiesQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    queryKey: [ENDPOINT],
  });

export default function useSearchActivities(
  searchParameters: ActivitiesFilters,
) {
  const queryString = JSON.stringify(searchParameters);
  const request: Request = {
    url: ENDPOINT,
    config: {
      method: 'post',
      data: { data: searchParameters },
    },
  };

  const requestConfig = {
    queryKey: [ENDPOINT, searchParameters.prospectId, queryString],
    queryFn: () => client(request),
  };

  const { data, ...rest } = useQuery<
    ResponsePaginated<PreviousActivitiesDTO[]>,
    CronoError
  >(requestConfig);

  return { data, ...rest };
}

export function useActivitiesSearchInfiniteScroll(
  searchParameters: Omit<ActivitiesFilters, 'page'>,
) {
  return useInfiniteQuery<
    ResponsePaginated<PreviousActivitiesDTO[]>,
    CronoError
  >({
    queryKey: [ENDPOINT, 'infinite', JSON.stringify(searchParameters)],
    queryFn: ({ pageParam = 1 }) => {
      const request: Request = {
        url: ENDPOINT,
        config: {
          method: 'post',
          data: { data: { ...searchParameters, page: pageParam } },
        },
      };
      return client(request);
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.data.data.length < searchParameters.limit) return undefined;
      return allPages.length + 1;
    },
  });
}
